import _ from "lodash";
import React, { useState } from "react";
import { useI18n } from "Hooks";
import withDerivedProps from "lib/withDerivedProps";

// components
import FullScreenLoader from "UIComponents/FullScreenLoader";

// third-party
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";

// utils and constants
import { getIsAITutorModalitiesEnabled } from "Utils/ACLStoreUtils";
import {
  ASSESSMENT_TYPE_AI_TUTOR,
  TASK_TYPE_AI_TUTOR,
} from "Constants/stringConstants";

// graphql
import { getAIConversationsQuery } from "./modules/AITutorChatResetButtonQueries";

// design system
import { Dialog, IconButton, Tooltip } from "@toddle-edu/ds-web";
import { RefreshArrowOutlined } from "@toddle-edu/ds-icons";

// modules
import { resetAIConversation } from "modules/Services";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

const AITutorChatResetButton = props => {
  const {
    aiConversationId,
    aiTutorSelectedTab,
    testId = "appComponents",
    assessmentType,
    isInitialState,
    taskTypeGroup,
    isGeneratingAIResponse = false,
  } = props;

  const [showDialog, setShowDialog] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  const { t } = useI18n();

  const onShowDialog = () => {
    setShowDialog(true);
  };

  const onHideDialog = () => {
    setShowDialog(false);
  };

  const onResetChat = async () => {
    if (aiConversationId) {
      setShowFullScreenLoader(true);
      await props.resetAIConversation({ input: { id: aiConversationId } });
      setShowFullScreenLoader(false);
    }

    onHideDialog();
  };

  if (
    !assessmentType ||
    (assessmentType !== ASSESSMENT_TYPE_AI_TUTOR &&
      taskTypeGroup !== TASK_TYPE_AI_TUTOR)
  ) {
    return null;
  }

  const showChatResetIcon =
    !isInitialState &&
    getIsAITutorModalitiesEnabled() &&
    aiTutorSelectedTab === "CHAT";

  return (
    <>
      {showChatResetIcon ? (
        <Tooltip
          dsVersion={"2.0"}
          showArrow={true}
          tooltip={t("classRoom:clear_chat")}
        >
          <IconButton
            testId={`${testId}-reset-chat`}
            variant={"neutral"}
            type={"outlined"}
            dsVersion={"2.0"}
            onClick={onShowDialog}
            disabled={isGeneratingAIResponse}
            icon={<RefreshArrowOutlined />}
          />
        </Tooltip>
      ) : null}

      {showDialog ? (
        <Dialog
          variant="delete-dialog"
          title={t("classRoom:clear_chat")}
          message={t("classRoom:clear_chat_history_msg")}
          buttonOneText={t("common:cancel")}
          buttonTwoText={t("common:clear")}
          onButtonOneClick={onHideDialog}
          onButtonTwoClick={onResetChat}
          onClose={onHideDialog}
          dsVersion={"2.0"}
        />
      ) : null}

      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </>
  );
};

const mapStateToProps = state => {
  const collaboratorsFilter = {
    userAcademicYearId: getSelectedAcademicYearId(state),
  };

  const aiData = _.get(state, "assessment.aiData", {});
  const aiTutorSelectedTab = _.get(aiData, "aiTutorSelectedTab");

  return { collaboratorsFilter, aiTutorSelectedTab };
};

const mapActionCreators = { resetAIConversation };

const getDerivedProps = props => {
  const { assessmentId, collaboratorsFilter } = props;

  const assessmentDetails = getAssessmentDetailsFromCache({
    id: assessmentId,
    collaboratorsFilter,
  });

  const assessmentType = _.get(assessmentDetails, "assessmentType.value", null);
  const taskTypeGroup = _.get(assessmentDetails, "taskType.type", null);
  const aiConversationEdges = _.get(
    assessmentDetails,
    "aiConversation.edges",
    []
  );

  const aiConversation = _.find(aiConversationEdges, ({ node }) => {
    const { entityType, entitySubType } = node || {};
    return entityType === "ASSESSMENT" && entitySubType === "TASK_CREATION";
  });

  const aiConversationId = _.get(aiConversation, "node.id", null);

  return { assessmentType, aiConversationId, taskTypeGroup };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  withDerivedProps(getDerivedProps),
  // fetches conversation messages
  graphql(getAIConversationsQuery, {
    name: "getAIConversations",
    skip: ({ aiConversationId }) => _.isEmpty(aiConversationId),
    options: ({ aiConversationId, portalType }) => ({
      fetchPolicy: "cache-only",
      variables: { id: aiConversationId, portalType },
      context: {
        useHttpLink: true,
      },
    }),
    props({ getAIConversations }) {
      const { node } = getAIConversations || {};

      const messages = _.get(node, "messages.edges", []);

      return {
        isInitialState: _.isEmpty(messages),
      };
    },
  })
)(AITutorChatResetButton);
